import { computed, type ComputedRef } from 'vue'

export default function useInputRange(props: {
    min: number
    max: number
    clamp?: {
        min: number
        max: number
    }
    step: number
}): {
    minFinal: ComputedRef<number>
    maxFinal: ComputedRef<number>
    rangeTotal: ComputedRef<number>
    rangeClamped: ComputedRef<number>
    clampPercentLeft: ComputedRef<number>
    clampPercentRight: ComputedRef<number>
    styleClampSpacerLeft: ComputedRef<string>
    styleClampSpacerRight: ComputedRef<string>
} {
    const minFinal = computed(() => {
        const min =
            props.clamp?.min && props.clamp.min >= props.min
                ? props.clamp.min
                : props.min

        if (0 === decimals.value) {
            // Min will be the previous lowest step if not exact
            return Math.floor(min)
        }

        const multiplier = Math.pow(10, decimals.value)

        // Min will be the previous lowest step if not exact
        return Math.floor(min * multiplier + props.step) / multiplier
    })

    const decimals = computed(() => {
        return props.step.toString().split('.')[1]?.length || 0
    })

    const maxFinal = computed(() => {
        const max =
            props.clamp?.max && props.clamp.max <= props.max
                ? props.clamp.max
                : props.max

        const numberOfSteps = Math.ceil((max - minFinal.value) / props.step)

        const final = minFinal.value + numberOfSteps * props.step

        if (0 === decimals.value) {
            // Max will be the next highest step for integer steps
            return Math.floor(final)
        }

        const multiplier = Math.pow(10, decimals.value)

        // With step such as 0.01 numbers may get to look like
        // 24.189999999999998 instead of 24.19
        // And external/default formatter(s) have no clue to how many decimals to .toFixed(n)
        // This will get it rounded after the calculations above and avoid the need to always pass formatters
        return Math.round(final * multiplier) / multiplier
    })

    const rangeTotal = computed(() => props.max - props.min)
    const rangeClamped = computed(() => maxFinal.value - minFinal.value)

    const clampPercentLeft = computed(
        () => ((minFinal.value - props.min) / rangeTotal.value) * 100
    )
    const clampPercentRight = computed(
        () => 100 - ((maxFinal.value - props.min) / rangeTotal.value) * 100
    )

    const styleClampSpacerLeft = computed(
        () => `flex: 0 0 ${clampPercentLeft.value}%`
    )

    const styleClampSpacerRight = computed(
        () => `flex: 0 0 ${clampPercentRight.value}%`
    )

    return {
        minFinal,
        maxFinal,
        rangeTotal,
        rangeClamped,
        clampPercentLeft,
        clampPercentRight,
        styleClampSpacerLeft,
        styleClampSpacerRight,
    }
}
